import { InfoOutlineIcon } from '@carboncopyco/icons';
import type { Opportunity } from '@carboncopyco/types';
import {
  ColorHex,
  MenuItem,
  Paper,
  RequiredAsterisk,
  Separator,
  TextBoxEditorTitle,
  TextCaption,
  Tooltip,
} from '@carboncopyco/ui';
import { capitalize } from '@carboncopyco/utils';
import { Combobox } from '@headlessui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useField, useFormikContext } from 'formik';
import React, { useRef, useState, type FocusEvent } from 'react';
import { ValidationErrorMessages } from '../../consts';

type OpportunitiesDropdownProps = {
  opportunities: Opportunity[];
  opportunityListTitle: string;
  isIntegrationConnected: boolean;
  integrationName: string;
  integrationLogo: React.ReactElement;
  clientFieldName?: string;
};

export const OpportunitiesDropdownField = React.forwardRef(
  OpportunitiesDropdownFieldInner
);

function OpportunitiesDropdownFieldInner<
  S extends { opportunity: Opportunity },
>(
  {
    opportunities,
    opportunityListTitle,
    isIntegrationConnected,
    integrationName,
    integrationLogo,
    clientFieldName = 'client',
  }: OpportunitiesDropdownProps,
  ref: React.ForwardedRef<any>
) {
  const [query, setQuery] = useState('');
  const filteredOpportunities =
    query === ''
      ? opportunities
      : opportunities.filter((opportunity) =>
          opportunity.name.toLowerCase().includes(query.toLowerCase())
        );

  const parentRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: filteredOpportunities.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 20,
  });

  const { values, setValues } = useFormikContext<S>();
  const [opportunityField, opportunityMeta] = useField({
    name: 'opportunity',
    validate: (value: Opportunity | null) => {
      if (!value?.name) {
        return ValidationErrorMessages.required;
      }
      return;
    },
  });

  const error = opportunityMeta.error;
  const isTouched = opportunityMeta.touched;
  const infoRef = useRef<HTMLDivElement>(null);

  return (
    <Combobox
      nullable
      value={opportunityField.value}
      onChange={(value) => {
        setValues({
          ...values,
          opportunity: value,
          [clientFieldName]: value?.name || '',
        });
      }}
    >
      {({ open }) => (
        <div className="w-full relative">
          <div className="flex">
            <Combobox.Label>
              <TextCaption
                label="Opportunity:"
                color="text-ds-natural-600"
              />
            </Combobox.Label>
            <RequiredAsterisk />
          </div>
          <div className="rounded border focus-within:border-ds-primary flex justify-between items-center py-2 px-2.5">
            <Combobox.Input
              ref={ref}
              onBlur={(event: FocusEvent<HTMLInputElement>) =>
                opportunityField.onBlur({
                  ...event,
                  target: {
                    ...event.target,
                    name: 'opportunity',
                  },
                })
              }
              placeholder="Acme, inc."
              className="placeholder:text-ds-natural grow border-0 focus:ring-0 bg-transparent p-0"
              onChange={(event) => setQuery(event.target.value)}
              displayValue={(opportunity: Opportunity) =>
                opportunity?.name || ''
              }
              autoComplete="off"
            />
            {opportunities.find(
              (opp) => opp.id === opportunityField.value?.id
            ) &&
              !open &&
              integrationLogo}
          </div>
          <Combobox.Options className="absolute w-full z-10">
            <Paper
              radius
              shadow="md"
            >
              <div className="flex flex-col max-h-[300px]">
                <div className="p-3 flex justify-between items-center">
                  <div className="flex gap-2">
                    <TextBoxEditorTitle
                      label={opportunityListTitle.toUpperCase()}
                    />
                    <div ref={infoRef}>
                      <InfoOutlineIcon
                        size={16}
                        color={ColorHex.natural}
                      />
                    </div>
                    <Tooltip
                      placement="top"
                      anchorRef={infoRef}
                      trigger="mouseenter"
                      autoCalcArrowPosition
                      arrowDown
                    >
                      <div className="w-36 p-2.5">
                        <TextCaption
                          label={`Associate opportunities from ${integrationName} for better tracking`}
                          color="text-ds-natural-600"
                        />
                      </div>
                    </Tooltip>
                  </div>
                  {integrationLogo}
                </div>
                <div
                  ref={parentRef}
                  className="px-3 pb-3 grow flex flex-col overflow-y-auto max-h-[130px]"
                >
                  {!isIntegrationConnected ? (
                    <div className="grow w-full flex flex-col items-center justify-center gap-2">
                      <TextCaption
                        label="Something went wrong"
                        color="text-ds-natural-600"
                      />
                      <TextCaption label="Please contact your admin to reconnect the integration" />
                    </div>
                  ) : filteredOpportunities.length ? (
                    <div
                      style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                      }}
                    >
                      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const opportunity =
                          filteredOpportunities[virtualRow.index];
                        return (
                          <Combobox.Option
                            key={virtualRow.index}
                            value={opportunity}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: `${virtualRow.size}px`,
                              transform: `translateY(${virtualRow.start}px)`,
                            }}
                          >
                            {({ active, disabled }) => (
                              <MenuItem
                                focus={active}
                                disabled={disabled}
                                item={{
                                  id: opportunity.id as string,
                                  label: capitalize(opportunity.name),
                                }}
                              />
                            )}
                          </Combobox.Option>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="grow w-full flex items-center justify-center">
                      <TextCaption label="Nothing matches your search" />
                    </div>
                  )}
                </div>
                {query.length > 0 && (
                  <>
                    <Separator horizontal />
                    <div className="p-1 shrink-0">
                      <Combobox.Option value={{ id: null, name: query }}>
                        {({ active }) => (
                          <MenuItem
                            focus={active}
                            item={{
                              id: 'freeformString',
                              label: `"${query}"`,
                            }}
                          />
                        )}
                      </Combobox.Option>
                    </div>
                  </>
                )}
              </div>
            </Paper>
          </Combobox.Options>
          {error && isTouched && (
            <div className="mx-1 -bottom-5 absolute w-full">
              <div className="whitespace-nowrap">
                <TextCaption
                  label={ValidationErrorMessages.required}
                  color="text-ds-danger-400"
                  lineHeight="leading-ds-100"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </Combobox>
  );
}
